import type { Viewport } from '../engine/hex/camera2d';
import type { Rect, Tile } from '../engine/hex/types';
import { isPointInRect } from '../engine/hex/hitTest';
import type { HexMapState, ActionRect } from '../engine/render/renderer';
import {
    clampRectToViewport,
    getCloseRect,
    getContentRect,
    getModalSize,
    MODAL_LAYOUT,
} from '../engine/render/modalLayout';
import type { ModalTheme } from './ui/ModalTheme';
import type { TabScrollState, UiItem } from './ui/items';
import { SectionItem } from './ui/items';
import { ContentBuilder } from './ui/ContentBuilder';
import { flowMeasureAndLayout, flowRender } from './ui/flowLayout';
import { t } from '../i18n/translations';
import { drawBuildingCostTooltip } from './ui/drawBuildingCostTooltip';

export abstract class AbstractModal {
    id: string;
    tile: Tile;
    rect: Rect;
    closeRect: Rect;
    scroll: number;
    contentHeight: number;
    actionRects: ActionRect[] = [];
    settlementId: string | null;
    showTileHighlight = true;
    allowCloseDuringTutorial = false;
    activeTabId: string | null = null;
    tabScroll: TabScrollState = { offset: 0, max: 0, rect: null };

    constructor(tile: Tile, rect: Rect, settlementId: string | null) {
        this.id = tile.id;
        this.tile = tile;
        this.rect = rect;
        this.closeRect = getCloseRect(rect);
        this.scroll = 0;
        this.contentHeight = 0;
        this.settlementId = settlementId;
    }

    render(
        ctx: CanvasRenderingContext2D,
        state: HexMapState,
        viewport: Viewport,
        theme: ModalTheme,
    ): void {
        const { w, h } = getModalSize(viewport);
        const rect = clampRectToViewport({ ...this.rect, w, h }, viewport);
        this.rect = rect;

        ctx.save();
        ctx.fillStyle = theme.colors.frameFill;
        ctx.strokeStyle = theme.colors.frameStroke;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.rect(rect.x, rect.y, rect.w, rect.h);
        ctx.fill();
        ctx.stroke();

        ctx.fillStyle = theme.colors.headerText;
        ctx.font = theme.fonts.header;
        ctx.textBaseline = 'top';
        ctx.fillText(
            this.getHeaderLabel(state),
            rect.x + MODAL_LAYOUT.sidePadding,
            rect.y + MODAL_LAYOUT.headerPaddingTop,
        );

        this.closeRect = getCloseRect(rect);
        ctx.strokeStyle = theme.colors.closeStroke;
        ctx.strokeRect(this.closeRect.x, this.closeRect.y, this.closeRect.w, this.closeRect.h);
        ctx.fillText(
            t(state.config.ui.language, 'modal.close'),
            this.closeRect.x + 5,
            this.closeRect.y + 2,
        );

        const contentRect = getContentRect(rect);
        const builder = new ContentBuilder();
        this.buildContent(state, builder);
        const items = builder.getItems();
        const layout = flowMeasureAndLayout(ctx, items, contentRect, theme);
        this.contentHeight = layout.contentHeight;
        this.actionRects = [];
        flowRender(ctx, {
            items,
            contentRect,
            scrollY: this.scroll,
            theme,
            hitTargetsOut: this.actionRects,
            icons: state.overlayAssets.icons,
        });

        const hover = findBuildingHover(items, state.pointer, this.scroll, contentRect);
        if (hover && this.settlementId) {
            const settlement = state.settlementsById[this.settlementId];
            if (settlement) {
                drawBuildingCostTooltip(ctx, state, theme, rect, hover, settlement);
            }
        }

        ctx.restore();
    }

    abstract getHeaderLabel(state: HexMapState): string;
    abstract buildContent(state: HexMapState, builder: ContentBuilder): void;

    handleAction(_actionId: string, _state: HexMapState): boolean {
        return false;
    }
}

function findBuildingHover(
    items: ReturnType<ContentBuilder['getItems']>,
    point: { x: number; y: number },
    scrollY: number,
    contentRect: Rect,
): { entry: { costs: Record<string, number> }; rect: Rect } | null {
    if (!isPointInRect(point, contentRect)) return null;
    return findHoverInItems(items, point, scrollY);
}

type HoverEntry = { entry: { costs: Record<string, number> }; rect: Rect };

function findHoverInItems(
    items: ReturnType<ContentBuilder['getItems']>,
    point: { x: number; y: number },
    scrollY: number,
): HoverEntry | null {
    for (const item of items) {
        const hoverable = getHoverable(item);
        if (hoverable) {
            const hover = hoverable.getHoverEntry(point, scrollY);
            if (hover && Object.keys(hover.entry.costs || {}).length > 0) return hover;
        }
        if (item instanceof SectionItem) {
            const nested = findHoverInItems(item.getChildren(), point, scrollY);
            if (nested) return nested;
        }
    }
    return null;
}

function getHoverable(item: UiItem): {
    getHoverEntry: (point: { x: number; y: number }, scrollY: number) => HoverEntry | null;
} | null {
    const maybe = item as {
        getHoverEntry?: (point: { x: number; y: number }, scrollY: number) => HoverEntry | null;
    };
    if (typeof maybe.getHoverEntry !== 'function') return null;
    return { getHoverEntry: maybe.getHoverEntry };
}
